import { API_QUERY_PARAMS, ApiEndPoints } from '../../../polly-manage.constants';
import {
  CreateWorkspacePayload,
  ESWorkspaceAttributes,
  FolderResponse, ModifyWorkspacePermissionsPayloadData,
  SelectedContentSortParams,
  WorkspaceContentItem,
  WorkspaceFileFilterParameters,
} from '../../interfaces';
import { WorkspaceResponse, ElasticApiResponse, ElasticCountApiResponse, CommonApiService, WorkspaceListResponse } from 'polly-commons';
import { Injectable } from '@angular/core';
import { PollyManageBackendService } from '../polly-manage-backend-service/polly-manage-backend.service';
import { PollyManageDataService } from '../polly-manage-data-service/polly-manage-data.service';
import { BehaviorSubject, forkJoin, Observable } from 'rxjs';
import { buildContentListingQuery, buildContentMetaDataListingQuery, buildPinnedItemListQuery, buildReportIdQuery } from '../../utilities/helper-functions/workspace-files-listing.utilities';
import {
  ES_WORKSPACE_CONTENT_ENDPOINT,
  ES_WORKSPACE_CONTENT_COUNT_ENDPOINT,
  fileTypeFilter,
  runsTypeFilter,
  folderTypeFilter,
} from '../../../modules/workspace-module/polly-manage-workspace.constants';
import { convertWorkspaceResponseToESResponse, getInitialESResponseObject } from '../../utilities/helper-functions/workspaces.utilities';
import { Params, Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class PollyManageWorkspaceService {
  public readonly isRootFolder: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public isRootFolder$: Observable<boolean> = this.isRootFolder.asObservable();
  private workspaceList!: ElasticApiResponse<ESWorkspaceAttributes, undefined>;

  constructor(
    private readonly manageBackendService: PollyManageBackendService,
    private readonly manageDataService: PollyManageDataService,
    private readonly router: Router,
    private readonly commonApiService: CommonApiService,
  ) {}

  /**
   * fetched workspace from data service
   */
  public getWorkspaceList(): void {
    this.manageDataService.workspaceList$.subscribe((resp: ElasticApiResponse<ESWorkspaceAttributes, undefined>) => {
      this.workspaceList = resp;
    });
  }

  /**
   * call post method in backend service to create a
   * new workspace
   */
  public createNewWorkspace(workspaceName: string, workspaceDesc?: string, tags?: string[], clientOrgId?: string): Observable<WorkspaceResponse> {
    const description: string = workspaceDesc ? workspaceDesc : '';
    const payloadWorkspace: CreateWorkspacePayload = {
      data: {
        type: ApiEndPoints.GET_WORKSPACES_ENDPOINT,
        attributes: {
          name: workspaceName,
          description,
          project_property: {
            type: ApiEndPoints.GET_WORKSPACES_ENDPOINT,
            labels: '',
          },
          tag_names: tags || [],
          ...(clientOrgId && { client_org_id: clientOrgId }),
        },
      },
    };
    return this.manageBackendService.createWorkspace(payloadWorkspace);
  }

  /**
   * adds the new workspace to the list of workspaces and
   * appends the workspace id in the url
   * @param workspace new workspace data
   */
  public addNewWorkspace(workspace: WorkspaceResponse): void {
    const esWorkspaceData: ESWorkspaceAttributes = convertWorkspaceResponseToESResponse(workspace, this.manageDataService.getCurrentUser());
    this.getWorkspaceList();
    if (!this.workspaceList) return;
    this.workspaceList.hits.hits.unshift(getInitialESResponseObject(esWorkspaceData));
    this.updateRouteWithWorkspaceId(workspace.data.id);
    this.manageDataService.setWorkspaceList(this.workspaceList);
    this.manageDataService.setSelectedWorkspace(esWorkspaceData);
  }

  /**
   * This function appends the workspace id query param to url
   */
  public updateRouteWithWorkspaceId(workSpaceId: string): void {
    const queryParams: Params = { id: workSpaceId };
    this.router.navigate([], {
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /**
   * TEMPORARY Hotfix function, which set the state to true/false to get the idea of whether
   * we are currently on folder or a root directory
   *
   * @param state having boolean value of whether we are in root or not
   */
  public changeStateOfIsRootFolder(state: boolean): void {
    this.isRootFolder.next(state);
  }

  /**
   * @description Fetched the workspace level content metadata i.e. no. of files/analysis using ElasticSearch APIs
   *
   * @param workspaceId For whichever workspace we want the contents metadata from
   * @return Observable<ElasticCountApiResponse[]>
   */
  public fetchContentMetaData(workspaceId: number): Observable<ElasticCountApiResponse[]> {
    const countUrl: string = ES_WORKSPACE_CONTENT_COUNT_ENDPOINT.replace('{workspace-id}', String(workspaceId));
    const analysisCountPayload = buildContentMetaDataListingQuery(workspaceId, runsTypeFilter);
    const filesCountPayload = buildContentMetaDataListingQuery(workspaceId, fileTypeFilter);
    const foldersCountPayload = buildContentMetaDataListingQuery(workspaceId, folderTypeFilter);
    return forkJoin<ElasticCountApiResponse[]>([
      this.manageBackendService.commonApiPostReq<ElasticCountApiResponse>(countUrl, analysisCountPayload),
      this.manageBackendService.commonApiPostReq<ElasticCountApiResponse>(countUrl, filesCountPayload),
      this.manageBackendService.commonApiPostReq<ElasticCountApiResponse>(countUrl, foldersCountPayload),
    ]);
  }

  /**
   * @description Fetches the workspace or folder level contents using Elastic search APIs
   *
   * @param from Pagination index for contents
   * @param workspaceId For whichever workspace we want the contents from
   * @param parentFolderId Workspace ID or folder ID for fetching the contents
   * @param searchString string to be searched
   * @param filters Workspace content filters
   * @param sortParams sorting params for workspace
   * @param filterByExtension Specific extension based filtering of content
   * @return Observable<ElasticApiResponse<WorkspaceContentItem, undefined>>
   */
  public fetchContentItems(
    from: number,
    workspaceId: number,
    parentFolderId?: string,
    searchString?: string,
    filters?: WorkspaceFileFilterParameters,
    sortParams?: SelectedContentSortParams,
    filterByExtension?: string[]
  ): Observable<ElasticApiResponse<WorkspaceContentItem, undefined>> {
    const payload = buildContentListingQuery(
      from,
      workspaceId,
      parentFolderId || workspaceId.toString(),
      searchString,
      sortParams,
      filters,
      filterByExtension
    );
    return this.manageBackendService.commonApiPostReq<ElasticApiResponse<WorkspaceContentItem, undefined>>(
      `${ES_WORKSPACE_CONTENT_ENDPOINT}/${workspaceId}/_search`,
      payload
    );
  }

  public getPinnedItems(
    workspaceId: number,
  ): Observable<ElasticApiResponse<WorkspaceContentItem, undefined>> {
    const payload = buildPinnedItemListQuery(
      workspaceId,
    );
    return this.manageBackendService.commonApiPostReq<ElasticApiResponse<WorkspaceContentItem, undefined>>(
      `${ES_WORKSPACE_CONTENT_ENDPOINT}/${workspaceId}/_search`,
      payload
    );
  }

  public getReportId(
    workspaceId: number,
    s3key: string
  ): Observable<ElasticApiResponse<WorkspaceContentItem, undefined>> {
    const payload = buildReportIdQuery(
      workspaceId,
      s3key
    );
    return this.manageBackendService.commonApiPostReq<ElasticApiResponse<WorkspaceContentItem, undefined>>(
      `${ES_WORKSPACE_CONTENT_ENDPOINT}/${workspaceId}/_search`,
      payload
    );
  }

  public removeUserFromWorkspace(url: string): Observable<unknown> {
    return this.commonApiService.delete(this.manageBackendService.getMithooUrl(url));
  }

  public updatePermission(url: string, payload: ModifyWorkspacePermissionsPayloadData): Observable<unknown> {
    return this.manageBackendService.commonApiPatchReq(url, payload);
  }

  /**
   * This function returns workspaces data.  The api is a postman mock api, which would be changed when real api is provided.
   *
   * @deprecated Use ES based functions instead with common API service
   */
  public getWorkspace(url: string): Observable<WorkspaceListResponse> {
    return this.manageBackendService.getReq(url);
  }


  /**
   * This function will get all the workspaces
   *
   * @deprecated Use ES based functions instead
   */
  public getAllWorkspaces(): Observable<WorkspaceListResponse> {
    const queryParam: string = encodeURI(API_QUERY_PARAMS.WORKSPACE_PAGE_SIZE_QUERY_PARAM);
    const url: string = `${ApiEndPoints.GET_WORKSPACES_ENDPOINT}?${queryParam}=1000`;
    return this.getWorkspace(url);
  }

  /**
   * @description Provides the observable with the list of all workspaces for which the current user is having
   * either edit or manage permissions.
   *
   * @return Observable<WorkspaceListResponse>
   *
   * @deprecated Use ES based functions instead
   */
  public getAllWorkspacesWithWritePermission(): Observable<WorkspaceListResponse> {
    const pageSizeQueryParam: string = encodeURI(API_QUERY_PARAMS.WORKSPACE_PAGE_SIZE_QUERY_PARAM);
    const permissionQueryParam: string = encodeURI(ApiEndPoints.WORKSPACE_WRITE_PERMISSION_PARAM);
    const permissionQueryParamValue: string = encodeURI(ApiEndPoints.WORKSPACE_WRITE_PERMISSION_VALUE);
    const url: string = `${ApiEndPoints.GET_WORKSPACES_ENDPOINT}?${pageSizeQueryParam}=1000&${permissionQueryParam}=${permissionQueryParamValue}`;
    return this.getWorkspace(url);
  }


  /**
   * This function makes multiple get requests and returns the response as an array
   * @param urls which is an array of api endpoints
   *
   * @deprecated Use ES Based functions instead with common API service
   */
  public getMultipleRequests(urls: string[]): Observable<any> {
    const mithooUrls: any[] = [];
    urls.forEach((element) => {
      const url = this.manageBackendService.getReq(element);
      mithooUrls.push(url);
    });
    return forkJoin(mithooUrls);
  }

  /**
   * This function fetches the folder contents
   * @param url of type string
   *
   * @deprecated Use ES based functions instead with common API service
   */
  public getFolderData(url: string): Observable<FolderResponse> {
    return this.manageBackendService.getReq(url);
  }
}
